import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Tabs, Collapse } from "antd";
import { CommentOutlined, DollarOutlined, FileTextOutlined, TeamOutlined, LinkOutlined } from "@ant-design/icons";
import styles from "./AffaireDetailsPage.module.scss";
import LoadingPlaceholder from "../../components/utils/LoadingPlaceholder";
import Main from "../../components/layouts/Main";
import { useUserContext } from "../../contexts/UserContext";
import Breadcrumb from "../../components/utils/Breadcrumb";
import AffaireEditForm from "../../components/affaire/AffaireEditForm";
import { AffaireService } from "../../services/AffaireService";
import AffaireDetails from "../../components/affaire/AffaireDetails";
import { catchError } from "../../services/DaoService";
import PieceJointeList from "../../components/piecejointe/PieceJointeList";
import TacheByAffaireList from "../../components/tache/TacheByAffaireList";
import MembreAffaireByAffaireList from "../../components/membreaffaire/MembreAffaireByAffaireList";
import AffaireLiensList from "../../components/affaire/AffaireLiensList";
import ActivityFeed from "../../components/Activity/ActiviteFeed";
import LogTraces from "../../components/logtrace/LogTraces";
import CommentaireFeed from "../../components/commentaire/CommentaireFeed";
import Path from "../../components/utils/Path";
import { EtapeService } from "../../services/EtapeService";
import { TacheService } from "../../services/TacheService";
import DepenseByAffaireList from "../../components/depense/DepenseByAffaireList";
import { useSelector } from "@legendapp/state/react";
import { state$ } from "../../state/globalState";
import { affairesState } from "../../state/actions/affairesState";
import AffaireHero from "../../components/affaire/AffaireHero";
import AffaireCloseConfirmDrawer from "../../components/affaire/AffaireCloseConfirmDrawer";
import Toast from "../../helpers/Toast";

const AffaireDetailsPage = () => {
  const { uid } = useParams();
  const { check, currentUser } = useUserContext();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const navigate = useNavigate();
  const [etapes, setEtapes] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [closeConfirmVisible, setCloseConfirmVisible] = useState(false);
  const [pendingStep, setPendingStep] = useState(null);
  const { loading } = useSelector(() => state$.affaires.get());
  const affaire = useSelector(() => state$.affaires.list.get().find(a => a.uid === uid));

  useEffect(() => { affairesState.getAffaire(uid); }, [uid]);

  useEffect(() => {
    EtapeService.getInstance()
      .findByAffaire(uid)
      .then(r => setEtapes(r.data))
      .catch(catchError);
  }, [uid]);

  useEffect(() => {
    setIsReadOnly(!!affaire?.done);
  }, [affaire]);

  const handleDelete = () => {
    affairesState.deleteAffaire(uid).then(() => navigate('/affaire'));
  };

  const handleUpdate = () => setEditModalVisible(false);

  const doChangeStep = (step) => {
    return AffaireService.getInstance()
      .changeStep(affaire.uid, { etape_id: step.id })
      .then(r => { affairesState.updateAffaireInList(r.data); Toast.success(r.message); })
      .catch(catchError);
  };

  const handleStepChange = async (step) => {
    // Si l'étape ciblée clôture le dossier, vérifier qu'il ne reste pas de
    // tâches non résolues avant de l'appliquer directement.
    if (step.pivot?.resolu) {
      try {
        const r = await TacheService.getInstance().search({ affaire_id: affaire.id, resolu: false });
        const unresolved = r.data.data ?? r.data ?? [];
        if (unresolved.length > 0) {
          setPendingStep(step);
          setCloseConfirmVisible(true);
          return;
        }
      } catch (e) { catchError(e); return; }
    }
    await doChangeStep(step);
  };

  if (loading || !affaire) return (
    <Main>
      <Breadcrumb parentLink="/affaire" canSee={!!check('VIEW-AFFAIRES')} parentLinkTitle="Dossiers" title="Chargement..." />
      <LoadingPlaceholder />
    </Main>
  );

  const canManageAffaire = affaire?.responsable_id === currentUser?.id || affaire?.user_id === currentUser?.id;
  const canEditStep = canManageAffaire;
  const canSeeLogs = canManageAffaire;
  const canManageMembers = affaire?.responsable_id === currentUser?.id || affaire?.user_id === currentUser?.id;
  const canManageTaches = canManageAffaire;
  const canComment = !!affaire?.current_membre || canManageAffaire;
  const canAttach = canComment;

  const tabItems = [
    {
      key: 'commentaires',
      label: <span><CommentOutlined /> Commentaires</span>,
      children: <CommentaireFeed isReadOnly={isReadOnly} canCreate={canComment} parentId={affaire.id} parentType={AffaireService.CLASS_NAME} />,
    },
    check('VIEW-DEPENSES') && {
      key: 'depenses',
      label: <span><DollarOutlined /> Dépenses</span>,
      children: <DepenseByAffaireList isReadOnly={isReadOnly} affaire={affaire} />,
    },
    canSeeLogs && {
      key: 'logs',
      label: <span><FileTextOutlined /> Logs</span>,
      children: <LogTraces target="affaire" parentId={affaire.id} />,
    },
  ].filter(Boolean);

  return (
    <Main>
      <Breadcrumb parentLink="/affaire" canSee={!!check('VIEW-AFFAIRES')} parentLinkTitle="Dossiers" title={affaire.nom} />

      <AffaireHero
        affaire={affaire}
        isReadOnly={isReadOnly}
        canManage={canManageAffaire}
        loading={loading}
        onEdit={() => setEditModalVisible(true)}
        onDelete={handleDelete}
        onRefresh={() => affairesState.getAffaire(uid)}
      />

      <div className={styles.pathContainer}>
        <Path
          readOnly={!canEditStep}
          currentStep={affaire.etape.code}
          steps={etapes}
          onCurrentPathChange={handleStepChange}
        />
      </div>

      <Row gutter={[20, 20]}>
        {/* ── Colonne principale ─────────────────────── */}
        <Col xs={24} lg={16}>
          <div className={styles.colStack}>
            {check('VIEW-TACHES') && (
              <TacheByAffaireList isReadOnly={isReadOnly} canManage={canManageTaches} affaire={affaire} />
            )}

            {check('VIEW-MEMBRE-AFFAIRES') && (
              <Collapse
                className={styles.card}
                defaultActiveKey={['collaborateurs']}
                items={[{
                  key: 'collaborateurs',
                  label: (
                    <span className={styles.collapseLabel}>
                      <TeamOutlined />
                      Collaborateurs
                    </span>
                  ),
                  children: <MembreAffaireByAffaireList isReadOnly={isReadOnly} canManage={canManageMembers} affaire={affaire} />,
                }]}
              />
            )}

            <Collapse
              className={styles.card}
              items={[{
                key: 'liens',
                label: (
                  <span className={styles.collapseLabel}>
                    <LinkOutlined />
                    Liens
                  </span>
                ),
                children: <AffaireLiensList affaire={affaire} canManage={canManageAffaire && !isReadOnly} />,
              }]}
            />

            <Collapse
              className={styles.card}
              defaultActiveKey={['documents']}
              items={[{
                key: 'documents',
                label: (
                  <span className={styles.collapseLabel}>
                    <FileTextOutlined />
                    Documents
                  </span>
                ),
                children: (
                  <PieceJointeList
                    parentId={affaire.id}
                    parentType={AffaireService.CLASS_NAME}
                    canAdd={canAttach}
                    canRemove={canManageAffaire}
                    canSee
                  />
                ),
              }]}
            />

            <div className={styles.tabsCard}>
              <Tabs
                defaultActiveKey="commentaires"
                items={tabItems}
              />
            </div>
          </div>
        </Col>

        {/* ── Sidebar ────────────────────────────────── */}
        <Col xs={24} lg={8}>
          <div className={styles.colStack}>
            <AffaireDetails isReadOnly={isReadOnly} affaire={affaire} />
            <ActivityFeed parentId={affaire.id} parentType={AffaireService.CLASS_NAME} />
          </div>
        </Col>
      </Row>

      {affaire && editModalVisible && (
        <AffaireEditForm
          visible={editModalVisible}
          uid={affaire.uid}
          onUpdate={handleUpdate}
          onCancel={() => setEditModalVisible(false)}
        />
      )}

      <AffaireCloseConfirmDrawer
        affaireId={affaire?.id}
        visible={closeConfirmVisible}
        onCancel={() => { setCloseConfirmVisible(false); setPendingStep(null); }}
        onConfirm={async () => {
          await doChangeStep(pendingStep);
          setCloseConfirmVisible(false);
          setPendingStep(null);
        }}
      />
    </Main>
  );
};

export default AffaireDetailsPage;
